// TTSGenerator.jsx
// Standalone component for TTS generation using cloned voices with Redux integration
// Updated to handle actual backend response structure

import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  LinearProgress,
  Chip,
  Stack,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  IconButton,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import {
  VolumeUp as SpeakerIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Send as GenerateIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

// Import Redux hooks
import { useGenerateTTSMutation } from '../../../redux/thunks/ai/generatetts';
import { useGetTTSStatusQuery } from '../../../redux/thunks/ai/getttsstatus';

const TTSGenerator = ({ 
  businessId, 
  businessName, 
  voice,
  accountType = 'free',
  onSuccess, 
  onCancel,
  onError 
}) => {
  // Form state
  const [text, setText] = useState('');
  const [emotionalInstructions, setEmotionalInstructions] = useState('');
  const [outputFormat, setOutputFormat] = useState('mp3');
  const [speed, setSpeed] = useState(1.0);
  const [stability, setStability] = useState(0.5);
  const [similarityBoost, setSimilarityBoost] = useState(0.5);
  
  // Generation state
  const [requestId, setRequestId] = useState(null);
  const [generatedAudio, setGeneratedAudio] = useState(null);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false); // Track completion success
  
  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  
  // History state
  const [ttsHistory, setTtsHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Advanced settings
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const audioRef = useRef(null);
  const playbackTimerRef = useRef(null);
  
  const token = useSelector((state) => state.auth?.token);

  // Redux hooks
  const [generateTTS, { 
    isLoading: isGenerating, 
    error: generateError 
  }] = useGenerateTTSMutation();

  const { 
    data: ttsStatus, 
    error: statusError, 
    isLoading: isPolling,
    refetch: refetchStatus
  } = useGetTTSStatusQuery(
    { requestId }, 
    { 
      skip: !requestId,
      pollingInterval: 2000, // Poll every 2 seconds
    }
  );

  console.log('ttsStatus', ttsStatus)

  // Character limits and quotas
  const characterLimits = {
    free: {
      maxPerRequest: 500,
      monthlyLimit: 500,
      description: '500 characters per month'
    },
    premium: {
      maxPerRequest: 1000,
      monthlyLimit: 5000,
      description: '5,000 characters per month'
    },
    enterprise: {
      maxPerRequest: 5000,
      monthlyLimit: 20000,
      description: '20,000 characters per month'
    }
  };

  const currentLimits = characterLimits[accountType] || characterLimits.free;

  // Emotional instruction presets
  const emotionalPresets = [
    { label: 'Professional', value: 'professional, clear, confident' },
    { label: 'Friendly', value: 'warm, welcoming, friendly' },
    { label: 'Excited', value: 'enthusiastic, energetic, excited' },
    { label: 'Calm', value: 'calm, soothing, relaxed' },
    { label: 'Authoritative', value: 'authoritative, commanding, serious' },
    { label: 'Casual', value: 'casual, conversational, relaxed' }
  ];

  // Sample texts for different business types
  const sampleTexts = {
    restaurant: "Thank you for calling our restaurant! We're currently open and taking reservations. Our chef's special today features fresh seasonal ingredients. Would you like to make a reservation or hear about our current menu?",
    retail: "Welcome to our store! We have a fantastic selection of new arrivals this week. Our team is here to help you find exactly what you're looking for. Don't forget to ask about our current promotions and loyalty program benefits.",
    service: "Thank you for your interest in our services. We provide professional, reliable solutions tailored to your specific needs. Our experienced team is ready to discuss your project and provide a customized quote. Let's schedule a consultation today.",
    healthcare: "Thank you for calling our practice. We're committed to providing you with quality healthcare in a comfortable environment. Our scheduling team can help you book an appointment or answer questions about our services.",
    general: "Hello and thank you for your interest in our business! We're here to help and excited to work with you. Our team is dedicated to providing exceptional service and value. Please let us know how we can assist you today."
  };

  // Debug logging for state changes
  useEffect(() => {
    console.log('TTSGenerator state changed:', {
      generatedAudio: generatedAudio ? 'EXISTS' : 'NULL',
      audioUrl: generatedAudio?.audioUrl ? 'EXISTS' : 'NULL',
      requestId,
      showSuccess,
      ttsHistoryLength: ttsHistory.length
    });
  }, [generatedAudio, requestId, showSuccess, ttsHistory.length]);

  // Handle TTS status updates
  useEffect(() => {
    if (ttsStatus) {
      console.log('TTS Status Update:', ttsStatus);
      
      if (ttsStatus.status === 'completed' && ttsStatus.results?.audioUrl) {
        const audioData = {
          audioUrl: ttsStatus.results.audioUrl,
          duration: ttsStatus.results.duration,
          fileSize: ttsStatus.results.fileSize,
          outputFormat: ttsStatus.results.outputFormat,
          metadata: ttsStatus.results.metadata,
          characterCount: ttsStatus.characterCount,
          generatedAt: ttsStatus.generatedAt,
          voiceName: ttsStatus.voiceName,
          voiceProvider: ttsStatus.voiceProvider
        };
        
        console.log('🎵 Setting generated audio:', audioData); // Debug log
        setGeneratedAudio(audioData);
        setShowSuccess(true); // Show success state
        
        // Add to history BEFORE clearing requestId
        setTtsHistory(prev => [
          {
            id: `${ttsStatus.requestId}-${Date.now()}`, // Make unique key
            requestId: ttsStatus.requestId,
            text: ttsStatus.text,
            audioUrl: ttsStatus.results.audioUrl,
            generatedAt: ttsStatus.generatedAt || new Date().toISOString(),
            characterCount: ttsStatus.characterCount,
            emotionalInstructions: emotionalInstructions,
            duration: ttsStatus.results.duration,
            fileSize: ttsStatus.results.fileSize,
            outputFormat: ttsStatus.results.outputFormat,
            voiceName: ttsStatus.voiceName,
            voiceProvider: ttsStatus.voiceProvider,
            processingTime: ttsStatus.results.metadata?.processingTime
          },
          ...prev.slice(0, 9) // Keep last 10
        ]);
        
        // Clear requestId LAST to stop polling
        setTimeout(() => {
          console.log('⏰ Clearing requestId after delay');
          setRequestId(null);
        }, 1000); // 1 second delay to ensure state is stable
        
        onSuccess && onSuccess({
          ...ttsStatus.results,
          characterCount: ttsStatus.characterCount,
          generatedAt: ttsStatus.generatedAt
        });
        
      } else if (ttsStatus.status === 'failed') {
        setError(ttsStatus.message || ttsStatus.error || 'TTS generation failed');
        setRequestId(null); // Stop polling
        onError && onError(ttsStatus.message || ttsStatus.error);
      }
      // For 'pending', 'processing', 'queued' - keep polling
    }
  }, [ttsStatus, emotionalInstructions, onSuccess, onError]);

  // Handle generation errors
  useEffect(() => {
    if (generateError) {
      console.error('TTS Generation Error:', generateError);
      setError(generateError.data?.message || generateError.message || 'Failed to start TTS generation');
      onError && onError(generateError);
    }
  }, [generateError, onError]);

  // Handle status polling errors
  useEffect(() => {
    if (statusError) {
      console.error('TTS Status Error:', statusError);
      setError('Failed to check generation status');
      setRequestId(null); // Stop polling
    }
  }, [statusError]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
      }
    };
  }, []);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle text change with validation
  const handleTextChange = (event) => {
    const newText = event.target.value;
    setText(newText);
    
    if (newText.length > currentLimits.maxPerRequest) {
      setError(`Text exceeds ${currentLimits.maxPerRequest} character limit for ${accountType} accounts.`);
    } else {
      setError(null);
    }
  };

  // Apply emotional preset
  const applyEmotionalPreset = (preset) => {
    setEmotionalInstructions(preset.value);
  };

  // Apply sample text
  const applySampleText = (businessType) => {
    setText(sampleTexts[businessType] || sampleTexts.general);
  };

  // Generate TTS using Redux mutation
  const handleGenerateTTS = async () => {
    if (!text.trim()) {
      setError('Please enter text to convert to speech.');
      return;
    }

    if (text.length > currentLimits.maxPerRequest) {
      setError(`Text exceeds ${currentLimits.maxPerRequest} character limit.`);
      return;
    }

    if (!voice || voice.status !== 'active') {
      setError('Voice is not ready. Please ensure voice processing is complete.');
      return;
    }

    setError(null);
    setGeneratedAudio(null);
    setShowSuccess(false); // Reset success state

    try {
      // Prepare the payload according to your backend API structure
      const payload = {
        auth: {
          token: token,
          authType: 'token'
        },
        text: text.trim(),
        businessId: businessId,
        parameters: {
          emotionalInstructions: emotionalInstructions.trim() || null,
          speed: speed,
          stability: stability,
          similarityBoost: similarityBoost,
          outputFormat: outputFormat
        }
      };

      console.log('Generating TTS with payload:', payload);
      
      const result = await generateTTS({ payload }).unwrap();
      
      console.log('TTS Generation started:', result);
      
      if (result.requestId) {
        setRequestId(result.requestId);
        // Polling will start automatically due to useGetTTSStatusQuery
      } else {
        setError('No request ID received from server');
      }

    } catch (err) {
      console.error('TTS Generation Failed:', err);
      setError(err.data?.message || err.message || 'Failed to generate TTS');
      onError && onError(err);
    }
  };

  // Audio playback controls
  const togglePlayback = () => {
    if (!audioRef.current || !generatedAudio) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
      }
    } else {
      audioRef.current.play();
      setIsPlaying(true);
      
      playbackTimerRef.current = setInterval(() => {
        if (audioRef.current) {
          setPlaybackTime(audioRef.current.currentTime);
        }
      }, 100);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setPlaybackTime(0);
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
    }
  };

  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (event, newValue) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newValue;
      setPlaybackTime(newValue);
    }
  };

  // Download audio
  const downloadAudio = () => {
    if (generatedAudio?.audioUrl) {
      const link = document.createElement('a');
      link.href = generatedAudio.audioUrl;
      link.download = `${businessName}-tts-${Date.now()}.${generatedAudio.outputFormat || outputFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Check if currently generating or polling
  const isProcessing = isGenerating || (requestId && isPolling);

  return (
    <Box>
      {/* Voice Information */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Using Voice:</strong> {voice?.voiceName || 'No Voice Selected'} • 
          <strong> Account Limit:</strong> {currentLimits.description}
        </Typography>
      </Alert>

      {/* Quick Start Samples */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Quick Start Templates
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {Object.keys(sampleTexts).map((businessType) => (
              <Chip
                key={businessType}
                label={businessType.charAt(0).toUpperCase() + businessType.slice(1)}
                onClick={() => applySampleText(businessType)}
                variant="outlined"
                size="small"
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Text Input */}
      <TextField
        fullWidth
        multiline
        rows={4}
        label="Text to Convert to Speech"
        value={text}
        onChange={handleTextChange}
        placeholder="Enter the text you want to convert to speech using your AI voice..."
        helperText={
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{text.length}/{currentLimits.maxPerRequest} characters</span>
            <span>Estimated duration: ~{Math.ceil(text.length / 150)} seconds</span>
          </Box>
        }
        error={text.length > currentLimits.maxPerRequest}
        sx={{ mb: 2 }}
      />

      {/* Emotional Instructions */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label="Emotional Instructions (Optional)"
          value={emotionalInstructions}
          onChange={(e) => setEmotionalInstructions(e.target.value)}
          placeholder="e.g., excited, calm, professional, friendly"
          helperText="Describe the desired tone or emotion for the speech"
          sx={{ mb: 1 }}
        />
        
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Quick presets:
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {emotionalPresets.map((preset) => (
            <Chip
              key={preset.label}
              label={preset.label}
              onClick={() => applyEmotionalPreset(preset)}
              variant="outlined"
              size="small"
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Stack>
      </Box>

      {/* Advanced Settings */}
      <Accordion expanded={showAdvanced} onChange={() => setShowAdvanced(!showAdvanced)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon fontSize="small" />
            <Typography variant="subtitle2">Advanced Settings</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Output Format</InputLabel>
                <Select
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value)}
                  label="Output Format"
                >
                  <MenuItem value="mp3">MP3 (Recommended)</MenuItem>
                  <MenuItem value="wav">WAV (High Quality)</MenuItem>
                  <MenuItem value="flac">FLAC (Lossless)</MenuItem>
                  <MenuItem value="ogg">OGG</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>
                Speech Speed: {speed}x
              </Typography>
              <Slider
                value={speed}
                onChange={(e, newValue) => setSpeed(newValue)}
                min={0.5}
                max={2.0}
                step={0.1}
                marks={[
                  { value: 0.5, label: 'Slow' },
                  { value: 1.0, label: 'Normal' },
                  { value: 2.0, label: 'Fast' }
                ]}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  Voice Stability: {stability}
                </Typography>
                <Tooltip title="Higher values make the voice more consistent but less expressive">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <Slider
                value={stability}
                onChange={(e, newValue) => setStability(newValue)}
                min={0.0}
                max={1.0}
                step={0.1}
                marks={[
                  { value: 0.0, label: 'Variable' },
                  { value: 0.5, label: 'Balanced' },
                  { value: 1.0, label: 'Stable' }
                ]}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  Similarity Boost: {similarityBoost}
                </Typography>
                <Tooltip title="Higher values make the voice sound more like the original sample">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <Slider
                value={similarityBoost}
                onChange={(e, newValue) => setSimilarityBoost(newValue)}
                min={0.0}
                max={1.0}
                step={0.1}
                marks={[
                  { value: 0.0, label: 'Creative' },
                  { value: 0.5, label: 'Balanced' },
                  { value: 1.0, label: 'Similar' }
                ]}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Generation Progress */}
      {isProcessing && (
        <Box sx={{ my: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              {isGenerating ? 'Starting TTS generation...' : 
               ttsStatus?.status === 'queued' ? 'TTS request queued...' :
               ttsStatus?.status === 'processing' ? (ttsStatus.progress?.message || 'Generating speech with your AI voice...') :
               ttsStatus?.message || 'Processing...'}
            </Typography>
          </Box>
          
          {/* Progress bar based on actual progress percentage */}
          {ttsStatus?.progress?.percentage ? (
            <LinearProgress 
              variant="determinate" 
              value={ttsStatus.progress.percentage} 
              sx={{ mb: 1 }}
            />
          ) : (
            <LinearProgress variant="indeterminate" sx={{ mb: 1 }} />
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {ttsStatus?.estimatedRemainingTime ? 
                `Est. remaining: ${ttsStatus.estimatedRemainingTime}` :
                'This usually takes 10-30 seconds'
              }
            </Typography>
            
            {ttsStatus?.progress && (
              <Typography variant="caption" color="text.secondary">
                {ttsStatus.progress.stage} - {ttsStatus.progress.percentage}%
              </Typography>
            )}
          </Box>
          
          {/* Additional status info */}
          {ttsStatus && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Request ID: {ttsStatus.requestId}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Voice: {ttsStatus.voiceName} ({ttsStatus.voiceProvider})
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Characters: {ttsStatus.characterCount} • Started: {new Date(ttsStatus.createdAt).toLocaleTimeString()}
              </Typography>
            </Box>
          )}
        </Box>
      )}

     
      {/* Success Alert */}
      {showSuccess && !isProcessing && (
        <Alert severity="success" sx={{ my: 2 }}>
          <Typography variant="body2">
            <strong>Speech Generated Successfully!</strong> Your AI voice has created audio using {generatedAudio?.characterCount || text.length} characters.
            {generatedAudio?.duration && ` Duration: ${Math.round(generatedAudio.duration)}s`}
          </Typography>
        </Alert>
      )}

      {/* Generated Audio Player - Always show if generatedAudio exists */}
      {generatedAudio && generatedAudio.audioUrl && (
        <Paper variant="outlined" sx={{ p: 2, my: 2, border: '2px solid', borderColor: 'success.main' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'success.main', fontWeight: 'bold' }}>
            ✓ Generated Speech Ready
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={isPlaying ? <PauseIcon /> : <PlayIcon />}
              onClick={togglePlayback}
            >
              {isPlaying ? 'Pause' : 'Play'}
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={downloadAudio}
            >
              Download
            </Button>
            
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <SpeakerIcon fontSize="small" color="success" />
              <Typography variant="caption">
                {formatTime(playbackTime)} / {formatTime(audioDuration)}
              </Typography>
            </Box>
          </Box>
          
          <Slider
            value={playbackTime}
            onChange={handleSeek}
            max={audioDuration}
            step={0.1}
            sx={{ mb: 1 }}
            color="success"
          />
          
          <audio
            ref={audioRef}
            src={generatedAudio.audioUrl}
            onEnded={handleAudioEnded}
            onLoadedMetadata={handleAudioLoadedMetadata}
            style={{ display: 'none' }}
          />
          
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Characters used: {generatedAudio.characterCount || text.length} • Format: {generatedAudio.outputFormat?.toUpperCase() || outputFormat.toUpperCase()} • 
              Speed: {speed}x • Generated: {generatedAudio.generatedAt ? new Date(generatedAudio.generatedAt).toLocaleTimeString() : new Date().toLocaleTimeString()}
              {generatedAudio.duration && ` • Duration: ${Math.round(generatedAudio.duration)}s`}
              {generatedAudio.fileSize && ` • Size: ${Math.round(generatedAudio.fileSize / 1024)}KB`}
              {generatedAudio.metadata?.processingTime && ` • Processing: ${Math.round(generatedAudio.metadata.processingTime / 1000)}s`}
            </Typography>
            
            {/* Voice and Provider Info */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Voice: {generatedAudio.voiceName || voice?.voiceName} • Provider: {generatedAudio.voiceProvider || 'ElevenLabs'}
              {generatedAudio.metadata?.voiceId && ` • Voice ID: ${generatedAudio.metadata.voiceId.substring(0, 8)}...`}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* TTS History - Persistent Audio Library */}
      <Paper variant="outlined" sx={{ p: 2, my: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">Audio Library</Typography>
            {ttsHistory.length > 0 && (
              <Chip label={`${ttsHistory.length} items`} size="small" color="primary" />
            )}
          </Box>
          <Button
            size="small"
            onClick={() => setShowHistory(!showHistory)}
            variant="outlined"
          >
            {showHistory ? 'Hide' : 'Show All'}
          </Button>
        </Box>

        {ttsHistory.length === 0 ? (
          <Alert severity="info">
            <Typography variant="body2">
              No audio generated yet. Your TTS generations will appear here for easy access and download.
            </Typography>
          </Alert>
        ) : (
          <Stack spacing={1}>
            {(showHistory ? ttsHistory : ttsHistory.slice(0, 3)).map((item) => (
              <Paper key={item.id} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 'medium', mb: 0.5 }}>
                      "{item.text.slice(0, 80)}{item.text.length > 80 ? '...' : ''}"
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {item.characterCount} characters • Generated {new Date(item.generatedAt).toLocaleString()}
                      {item.duration && ` • ${Math.round(item.duration)}s`}
                      {item.processingTime && ` • Processed in ${Math.round(item.processingTime / 1000)}s`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Voice: {item.voiceName} • {item.outputFormat?.toUpperCase()} • {Math.round((item.fileSize || 0) / 1024)}KB
                      {item.emotionalInstructions && ` • Tone: ${item.emotionalInstructions}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PlayIcon />}
                      onClick={() => window.open(item.audioUrl, '_blank')}
                    >
                      Play
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = item.audioUrl;
                        link.download = `${businessName}-tts-${item.requestId}.${item.outputFormat || 'mp3'}`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      Download
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ))}
            
            {!showHistory && ttsHistory.length > 3 && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                +{ttsHistory.length - 3} more audio files available
              </Typography>
            )}
          </Stack>
        )}
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      {/* Usage Tips */}
      <Alert severity="success" sx={{ my: 2 }}>
        <Typography variant="body2">
          <strong>Pro Tips:</strong> Use punctuation for natural pauses. 
          Emotional instructions control tone and delivery. 
          Generated audio is perfect for customer service, announcements, or marketing content.
        </Typography>
      </Alert>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={onCancel} disabled={isProcessing}>
          Close
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {generatedAudio && (
            <Button
              variant="outlined"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={downloadAudio}
            >
              Download Audio
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleGenerateTTS}
            disabled={
              !text.trim() || 
              text.length > currentLimits.maxPerRequest || 
              isProcessing || 
              !voice || 
              voice.status !== 'active'
            }
            startIcon={isProcessing ? <CircularProgress size={20} /> : <GenerateIcon />}
          >
            {isProcessing ? 'Generating...' : generatedAudio ? 'Generate Another' : 'Generate Speech'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TTSGenerator;